import type {Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import {generateUuid} from "@vesta/global-common-typescript/utilities/utilities";
import {type FilterConfiguration, type FilterConfigurationLineItem} from "~/components/ottopilotDataViewer/filterConfiguration";
import {FilterOperation} from "@vesta/vos-types/modules/workspaceLevel/domain/FilterOperation";

export const AON_MS_PER_DAY = 86_400_000;

export type AonWorkspaceConfig = {
    displayColumnId: string;
    rangesColumnId: string;
    proxyColumnId?: string;
    createdAtPickerColumnId: string;
    createdAtApiColumn: string;
};

export type AonSrimanFilterGroup = {
    logic: "AND" | "OR";
    filters: Array<
        | {
              column: string;
              operator: string;
              value: unknown;
          }
        | AonSrimanFilterGroup
    >;
};

export function computeAonDaysFromCreatedAtIso(createdAt: string): number | null {
    const trimmed = String(createdAt).trim();
    if (trimmed === "") {
        return null;
    }
    const createdMs = new Date(trimmed).getTime();
    if (Number.isNaN(createdMs) === true) {
        return null;
    }
    const ageDays = Math.floor((Date.now() - createdMs) / AON_MS_PER_DAY);
    return Math.max(0, ageDays);
}

export function parseAonFilterDayValues(selectedItems: ReadonlyArray<string>): Array<number> {
    const days: Array<number> = [];
    for (const item of selectedItems) {
        const trimmed = String(item).trim();
        if (trimmed === "") {
            continue;
        }
        const parsed = parseInt(trimmed, 10);
        if (Number.isNaN(parsed) !== true && parsed >= 0) {
            days.push(parsed);
        }
    }
    return days;
}

export function buildDistinctAonFilterValuesFromCreatedAtColumnValues(createdAtColumnValues: ReadonlyArray<string>): Array<string> {
    const aonValues = new Set<string>();
    for (const createdAtValue of createdAtColumnValues) {
        const aonDays = computeAonDaysFromCreatedAtIso(createdAtValue);
        if (aonDays !== null) {
            aonValues.add(String(aonDays));
        }
    }
    return Array.from(aonValues).sort((left, right) => parseInt(left, 10) - parseInt(right, 10));
}

export function computeCreatedAtLteForAonDays(aonDays: number): string {
    const cutoffMs = Date.now() - aonDays * AON_MS_PER_DAY;
    return new Date(cutoffMs).toISOString();
}

export function computeCreatedAtGteForAonDays(aonDays: number): string {
    const cutoffMs = Date.now() - (aonDays + 1) * AON_MS_PER_DAY + 1;
    return new Date(cutoffMs).toISOString();
}

export function buildAonCreatedAtRangeFilterGroup(createdAtColumn: string, aonDays: number): AonSrimanFilterGroup {
    return {
        logic: "AND",
        filters: [
            {
                column: createdAtColumn,
                operator: "greaterThanOrEqualTo",
                value: computeCreatedAtGteForAonDays(aonDays),
            },
            {
                column: createdAtColumn,
                operator: "lessThanOrEqualTo",
                value: computeCreatedAtLteForAonDays(aonDays),
            },
        ],
    };
}

export function buildAonCreatedAtOrFilterGroup(createdAtColumn: string, aonDaysList: ReadonlyArray<number>): AonSrimanFilterGroup | null {
    if (aonDaysList.length === 0) {
        return null;
    }
    const rangeGroups = aonDaysList.map((aonDays) => buildAonCreatedAtRangeFilterGroup(createdAtColumn, aonDays));
    if (rangeGroups.length === 1) {
        return rangeGroups[0] ?? null;
    }
    return {
        logic: "OR",
        filters: rangeGroups,
    };
}

export function isAonDisplayColumnId(config: AonWorkspaceConfig, columnId: string): boolean {
    return columnId === config.displayColumnId;
}

export function isAonFilterRangesLineItem(config: AonWorkspaceConfig, lineItem: FilterConfigurationLineItem): boolean {
    return lineItem.columnId === config.rangesColumnId && lineItem.operation === FilterOperation.in;
}

export function isAonUiOnlyFilterLineItem(config: AonWorkspaceConfig, lineItem: FilterConfigurationLineItem): boolean {
    return isAonDisplayColumnId(config, lineItem.columnId) === true && lineItem.operation === FilterOperation.in;
}

export function partitionAonFilterLineItems(
    config: AonWorkspaceConfig,
    lineItems: ReadonlyArray<FilterConfigurationLineItem>,
): {
    otherLineItems: Array<FilterConfigurationLineItem>;
    aonRangesLineItems: Array<FilterConfigurationLineItem>;
} {
    const otherLineItems: Array<FilterConfigurationLineItem> = [];
    const aonRangesLineItems: Array<FilterConfigurationLineItem> = [];
    for (const lineItem of lineItems) {
        if (isAonFilterRangesLineItem(config, lineItem) === true) {
            aonRangesLineItems.push(lineItem);
        } else {
            otherLineItems.push(lineItem);
        }
    }
    return {
        otherLineItems: otherLineItems,
        aonRangesLineItems: aonRangesLineItems,
    };
}

export function buildAonCreatedAtFilterLineItems(config: AonWorkspaceConfig, selectedAonDayValues: ReadonlyArray<string>): Array<FilterConfigurationLineItem> {
    const days = parseAonFilterDayValues(selectedAonDayValues);
    if (days.length === 0) {
        return [];
    }
    return [
        {
            columnId: config.rangesColumnId,
            operation: FilterOperation.in,
            values: days.map((aonDays) => String(aonDays)),
        },
    ];
}

export function mapAonFilterLineItemForApi(config: AonWorkspaceConfig, lineItem: FilterConfigurationLineItem): Array<FilterConfigurationLineItem> {
    if (isAonUiOnlyFilterLineItem(config, lineItem) === true) {
        return [];
    }
    if (isAonFilterRangesLineItem(config, lineItem) === true) {
        return [lineItem];
    }
    if (config.proxyColumnId !== undefined && lineItem.columnId === config.proxyColumnId) {
        if (lineItem.operation === FilterOperation.greaterThanEqualTo) {
            return [
                {
                    columnId: config.createdAtApiColumn,
                    operation: FilterOperation.greaterThanEqualTo,
                    value: lineItem.value,
                },
            ];
        }
        if (lineItem.operation === FilterOperation.lessThanEqualTo) {
            return [
                {
                    columnId: config.createdAtApiColumn,
                    operation: FilterOperation.lessThanEqualTo,
                    value: lineItem.value,
                },
            ];
        }
        return [];
    }
    return [lineItem];
}

export function collectAonDaysFromRangesLineItems(config: AonWorkspaceConfig, rangesLineItems: ReadonlyArray<FilterConfigurationLineItem>): Array<number> {
    const days = new Set<number>();
    for (const lineItem of rangesLineItems) {
        if (isAonFilterRangesLineItem(config, lineItem) !== true) {
            continue;
        }
        if (lineItem.operation !== FilterOperation.in) {
            continue;
        }
        for (const parsed of parseAonFilterDayValues(lineItem.values)) {
            days.add(parsed);
        }
    }
    return Array.from(days).sort((left, right) => left - right);
}

export function buildAonCreatedAtOrFilterGroupFromRangesLineItems(
    config: AonWorkspaceConfig,
    rangesLineItems: ReadonlyArray<FilterConfigurationLineItem>,
): AonSrimanFilterGroup | null {
    const aonDaysList = collectAonDaysFromRangesLineItems(config, rangesLineItems);
    return buildAonCreatedAtOrFilterGroup(config.createdAtApiColumn, aonDaysList);
}

export function buildAonCustomFilter(config: AonWorkspaceConfig, aonDisplayColumnId: string) {
    return (args: {
        controller: {
            setFilterConfiguration: (updater: (prev: FilterConfiguration) => FilterConfiguration) => void;
        };
        ephemeralValue: {
            revisionId: Uuid;
            lineItems: Array<string> | null;
        };
        column: {
            id: string;
            displayName: string;
        };
        closePopup: () => void;
    }) => {
        const selectedItems = args.ephemeralValue.lineItems;
        const stripAonDerivedLineItems = (lineItem: FilterConfigurationLineItem): boolean => {
            if (config.proxyColumnId !== undefined && lineItem.columnId === config.proxyColumnId) {
                return false;
            }
            if (lineItem.columnId === config.rangesColumnId) {
                return false;
            }
            if (lineItem.columnId === aonDisplayColumnId) {
                return false;
            }
            if (isAonDisplayColumnId(config, lineItem.columnId) === true) {
                return false;
            }
            return true;
        };

        if (selectedItems === null || selectedItems.length === 0) {
            args.controller.setFilterConfiguration((prev) => ({
                revisionId: generateUuid(),
                lineItems: prev.lineItems.filter(stripAonDerivedLineItems),
            }));
            args.closePopup();
            return;
        }

        const aonFilterLineItems = buildAonCreatedAtFilterLineItems(config, selectedItems);
        args.controller.setFilterConfiguration((prev) => ({
            revisionId: generateUuid(),
            lineItems: [
                ...prev.lineItems.filter(stripAonDerivedLineItems),
                ...aonFilterLineItems,
                {
                    columnId: aonDisplayColumnId,
                    operation: FilterOperation.in,
                    values: [...selectedItems],
                },
            ],
        }));
        args.closePopup();
    };
}
